import React, { useCallback, useMemo } from "react";
import { Layout, DatePicker } from "antd";
import dayjs from "dayjs";
import HistoryResults from "./HistoryResults";
import { useGetHistorySegmentsQuery } from "../../../Redux/service/mod-streaming/ModData";
import "./HistorySidebar.css";
import trans from "../../../utils";

const { Sider } = Layout;
const { RangePicker } = DatePicker;

const HistorySidebar = React.memo(({
  selectedVehicle,
  selectedChannel,
  dateFilter,
  setDateFilter,
  customDateRange,
  setCustomDateRange,
  selectedSegment,
  setSelectedSegment,
}) => {
  // Calculate start and end time based on filter
  const dateRange = useMemo(() => {
    const today = dayjs();
    const yesterday = dayjs().subtract(1, "day");
    
    switch (dateFilter) {
      case "today":
        return {
          startTime: today.startOf("day").toISOString(),
          endTime: today.endOf("day").toISOString(),
        };
      case "yesterday":
        return {
          startTime: yesterday.startOf("day").toISOString(),
          endTime: yesterday.endOf("day").toISOString(),
        };
      case "custom":
        if (customDateRange && customDateRange[0] && customDateRange[1]) {
          return {
            startTime: customDateRange[0].startOf("day").toISOString(),
            endTime: customDateRange[1].endOf("day").toISOString(),
          };
        }
        return null;
      default:
        return {
          startTime: today.startOf("day").toISOString(),
          endTime: today.endOf("day").toISOString(),
        };
    }
  }, [dateFilter, customDateRange]);

  // Fetch history segments from API
  const { data: segmentsData, isLoading: segmentsLoading } = useGetHistorySegmentsQuery(
    {
      imei: selectedVehicle?.imei,
      startTime: dateRange?.startTime,
      endTime: dateRange?.endTime,
      channel: selectedChannel?.number,
      streamType: 0,
      alarmOnly: false,
    },
    {
      skip: !selectedVehicle || !selectedChannel || !dateRange,
    }
  );

  const historySegments = useMemo(() => {
    if (!segmentsData?.files || !Array.isArray(segmentsData.files)) {
      return [];
    }
    return segmentsData.files.map((file, index) => {
      const startTime = file.startTime;
      const endTime = file.endTime;
      
      // Format times for display (HH:mm:ss)
      const formattedStartTime = formatTime(startTime);
      const formattedEndTime = formatTime(endTime);
      const duration = calculateDuration(startTime, endTime);
      
      return {
        id: `segment-${index}-${startTime}`,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        startTimeISO: startTime, // Keep ISO format for API calls
        endTimeISO: endTime, // Keep ISO format for API calls
        duration: duration,
        file: file, // Keep full file object for reference
      };
    });
  }, [segmentsData]);

  const handleDateFilterChange = useCallback((filter) => {
    setDateFilter(filter);
    setSelectedSegment(null); // Reset selection when filter changes
  }, [setDateFilter, setSelectedSegment]);

  const handleCustomDateRangeChange = useCallback((dates) => {
    setCustomDateRange(dates);
    setSelectedSegment(null); // Reset selection when date range changes
  }, [setCustomDateRange, setSelectedSegment]);

  return (
    <Sider width={320} className="history-sidebar" theme="light">
      <div className="history-sidebar-header">
        <h3 className="sidebar-title">{trans("History")}</h3>
        <div className="selected-channel-info">
          {selectedVehicle?.name} - {selectedChannel?.name}
        </div>
      </div>

      <div className="history-sidebar-content">
        <div className="date-filter-section">
          <h4 className="filter-section-title">{trans("Date Filter")}</h4>
          <div className="date-filter-buttons">
            <button
              className={`date-filter-btn ${dateFilter === "today" ? "active" : ""}`}
              onClick={() => handleDateFilterChange("today")}
              type="button"
            >
              {trans("Today")}
            </button>
            <button
              className={`date-filter-btn ${dateFilter === "yesterday" ? "active" : ""}`}
              onClick={() => handleDateFilterChange("yesterday")}
              type="button"
            >
              {trans("Yesterday")}
            </button>
            <button
              className={`date-filter-btn ${dateFilter === "custom" ? "active" : ""}`}
              onClick={() => handleDateFilterChange("custom")}
              type="button"
            >
              {trans("Custom Date")}
            </button>
          </div>

          {dateFilter === "custom" && (
            <div className="custom-date-picker">
              <RangePicker
                value={customDateRange}
                onChange={handleCustomDateRangeChange}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                placeholder={[trans("Start Date"), trans("End Date")]}
              />
            </div>
          )}
        </div>

        <HistoryResults
          segments={historySegments}
          selectedSegment={selectedSegment}
          setSelectedSegment={setSelectedSegment}
          isLoading={segmentsLoading}
        />
      </div>
    </Sider>
  );
});

// Helper function to format time (returns HH:mm:ss)
function formatTime(timeString) {
  if (!timeString) return "N/A";
  try {
    const time = dayjs(timeString);
    if (time.isValid()) {
      return time.format("HH:mm:ss");
    }
    return "N/A";
  } catch (e) {
    return "N/A";
  }
}

// Helper function to calculate duration
function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return "N/A";
  
  try {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    if (!start.isValid() || !end.isValid()) {
      return "N/A";
    }
    const diff = end.diff(start, "second");
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  } catch (e) {
    return "N/A";
  }
}

export default HistorySidebar;
